// دیالوگ‌های وظیفه پیگیری (SCR-STAFF-962؛ US-ENG-020): ثبت با موضوع، نوع، موعد، مسئول، اولویت و هدف (AC-ENG-020.1)؛ انجام با نتیجه الزامی و «تماس بعدی» با
// موعد (BR-ENG-020؛ AC-ENG-020.2)؛ پیام سریع متن آزاد (فقط پاسخ به درخواست خود موضوع — BR-ENG-002).
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type EngAssigneeOption, type EngFollowUpSubjectKind, type EngFollowUpView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, faDigits, FormDialog, SelectRHF, TextFieldRHF, zBusinessDate, zRequiredText } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { AssigneePicker, DEFAULT_DUE_TIME, DueFields, engKeys, FOLLOWUP_OUTCOMES, FOLLOWUP_TYPES, localDate, PRIORITIES, useLocalInstant } from './common';

const TIME = /^\d{2}:\d{2}$/;

const createSchema = z.object({
  type: z.enum(['Call', 'Message', 'InternalReminder']),
  priority: z.enum(['High', 'Normal', 'Low']),
  goal: zRequiredText(300),
  date: zBusinessDate(),
  time: z.string().regex(TIME, { error: 'common:validation.required' }),
});
type CreateForm = z.input<typeof createSchema>;

export interface FollowUpSubject {
  kind: EngFollowUpSubjectKind;
  id: string;
  name: string;
}

/** ثبت وظیفه پیگیری برای سرنخ یا مراجعه‌کننده (پیش‌فرض موعد ۳ روز بعد ساعت ۱۰). */
export function FollowUpDialog({
  subject,
  consultationId,
  defaultGoal,
  onClose,
}: {
  subject: FollowUpSubject;
  consultationId?: string;
  defaultGoal?: string;
  onClose: (saved?: EngFollowUpView) => void;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const toInstant = useLocalInstant();
  const [assignee, setAssignee] = useState<EngAssigneeOption | null>(null);
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { type: 'Call', priority: 'Normal', goal: defaultGoal ?? '', date: localDate(3), time: DEFAULT_DUE_TIME },
  });
  const save = useMutation({
    mutationFn: (form: CreateForm) =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/followups', {
          body: {
            subjectKind: subject.kind,
            subjectId: subject.id,
            type: form.type,
            dueAt: toInstant(form.date, form.time),
            assigneeMembershipId: assignee?.membershipId ?? null,
            priority: form.priority,
            goal: form.goal,
            consultationId: consultationId ?? null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: engKeys.all(session?.tenantId) });
      onClose(saved);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['goal', 'type', 'priority'], codeFields: { 'PHX-ENG-0103': 'goal' } });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-962-NEW"
      titleKey="eng:followup_form.title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2" data-testid="followup-subject">
          {t(`eng:subject_kind.${subject.kind}`)}: {subject.name}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SelectRHF control={control} name="type" labelKey="eng:followup_form.type" fullWidth options={FOLLOWUP_TYPES.map((value) => ({ value, labelKey: `eng:followup_type.${value}` }))} />
          <SelectRHF control={control} name="priority" labelKey="eng:followup_form.priority" fullWidth options={PRIORITIES.map((value) => ({ value, labelKey: `eng:priority.${value}` }))} />
        </Stack>
        <TextFieldRHF control={control} name="goal" labelKey="eng:followup_form.goal" helperKey="eng:common.no_clinical_hint" required fullWidth inputProps={{ 'data-testid': 'followup-goal' }} />
        <DueFields control={control} dateName="date" timeName="time" testId="followup-due" />
        <AssigneePicker value={assignee} onChange={setAssignee} labelKey="eng:followup_form.assignee" testId="followup-assignee" />
      </Stack>
    </FormDialog>
  );
}

const completeSchema = z
  .object({
    outcome: z.string().min(1, { error: 'eng:validation.outcome_required' }),
    note: z.string().max(1000, { error: 'common:validation.max_length' }),
    date: z.string(),
    time: z.string(),
  })
  .superRefine((value, context) => {
    if (value.outcome === 'CallAgain' && !value.date) {
      context.addIssue({ code: 'custom', path: ['date'], message: 'eng:validation.next_due_required' });
    }
    if (value.outcome === 'CallAgain' && !TIME.test(value.time)) {
      context.addIssue({ code: 'custom', path: ['time'], message: 'eng:validation.next_due_required' });
    }
  });
type CompleteForm = z.input<typeof completeSchema>;

/** انجام وظیفه با نتیجه الزامی (AC-ENG-020.2؛ FLOW-15: ثبت نتیجه ≤ ۱۰ ثانیه). */
export function CompleteFollowUpDialog({ activity, onClose }: { activity: EngFollowUpView; onClose: (saved?: EngFollowUpView) => void }) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const toInstant = useLocalInstant();
  const outcomeId = useId();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<CompleteForm>({
    resolver: zodResolver(completeSchema),
    defaultValues: { outcome: '', note: '', date: localDate(1), time: DEFAULT_DUE_TIME },
  });
  const outcome = useWatch({ control, name: 'outcome' });
  const save = useMutation({
    mutationFn: (form: CompleteForm) =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/followups/{id}:complete', {
          params: { path: { id: activity.id } },
          body: {
            outcome: form.outcome as never,
            note: form.note || null,
            nextDueAt: form.outcome === 'CallAgain' ? toInstant(form.date, form.time) : null,
          },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: engKeys.all(session?.tenantId) });
      onClose(saved);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['outcome', 'note'], codeFields: { 'PHX-ENG-0105': 'outcome' } });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-962-DONE"
      titleKey="eng:complete_form.title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          {activity.subjectName ?? '—'} · {activity.goal}
        </Typography>
        <Controller
          control={control}
          name="outcome"
          render={({ field, fieldState }) => (
            <FormControl error={Boolean(fieldState.error)} required>
              <FormLabel id={outcomeId}>{t('eng:complete_form.outcome')}</FormLabel>
              <RadioGroup aria-labelledby={outcomeId} value={field.value} onChange={(event) => field.onChange(event.target.value)} data-testid="complete-outcome">
                {FOLLOWUP_OUTCOMES.map((value) => (
                  <FormControlLabel key={value} value={value} control={<Radio />} label={t(`eng:followup_outcome.${value}`)} />
                ))}
              </RadioGroup>
              {fieldState.error?.message && <FormHelperText>{t(fieldState.error.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        {outcome === 'CallAgain' && <DueFields control={control} dateName="date" timeName="time" testId="complete-next" />}
        <TextFieldRHF control={control} name="note" labelKey="eng:complete_form.note" helperKey="eng:common.no_clinical_hint" multiline minRows={2} fullWidth />
      </Stack>
    </FormDialog>
  );
}

const messageSchema = z.object({ text: zRequiredText(330) });
type MessageForm = z.input<typeof messageSchema>;

/** پیام سریع از صف (C-AGENT-977): متن آزاد Transactional از MSG. */
export function MessageDialog({ activity, onClose }: { activity: EngFollowUpView; onClose: (sent?: boolean) => void }) {
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<MessageForm>({ resolver: zodResolver(messageSchema), defaultValues: { text: '' } });
  const text = useWatch({ control, name: 'text' });
  const send = useMutation({
    mutationFn: (form: MessageForm) =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/followups/{id}:send-message', {
          params: { path: { id: activity.id } },
          body: { text: form.text },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => onClose(true),
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['text'], codeFields: { 'PHX-ENG-0103': 'text' } });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-962-MSG"
      titleKey="eng:message_form.title"
      submitKey="eng:message_form.send"
      onSubmit={() => void handleSubmit((form) => send.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={send.isPending}
      submitError={submitError}
    >
      <Stack spacing={1}>
        <Alert severity="info">{activity.subjectName ?? '—'}</Alert>
        <TextFieldRHF control={control} name="text" labelKey="eng:message_form.text" helperKey="eng:message_form.hint" multiline minRows={3} fullWidth required inputProps={{ 'data-testid': 'message-text', maxLength: 330 }} />
        <Typography variant="caption" color="text.secondary">
          {faDigits(`${(text ?? '').length}/330`)}
        </Typography>
      </Stack>
    </FormDialog>
  );
}
